import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error("Failed to load Razorpay"));
    document.body.appendChild(script);
  });

const formatInr = (n) => {
  const v = parseFloat(n);
  if (!Number.isFinite(v)) return "₹0.00";
  return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const PayInvoicePage = () => {
  const { token } = useParams();
  const url = API_BASE_URL;
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payFull, setPayFull] = useState(true);

  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${url}/invoice-pay/${token}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Unable to load invoice.");
        setInvoice(null);
        return;
      }
      setInvoice(data);
      setPayAmount(String(parseFloat(data.pending) || ""));
      setPayFull(true);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token, url]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handlePay = async () => {
    if (!invoice || paying) return;
    setPaying(true);
    setError("");

    const pending = parseFloat(invoice.pending) || 0;
    const amountToPay = payFull
      ? pending
      : parseFloat(payAmount) || 0;

    if (amountToPay <= 0) {
      setError("Enter a payment amount greater than zero.");
      setPaying(false);
      return;
    }
    const minPay = parseFloat(invoice.min_payment) || 1;
    if (amountToPay < minPay - 0.001) {
      setError(`Minimum online payment is ${formatInr(minPay)}.`);
      setPaying(false);
      return;
    }
    if (amountToPay > pending + 0.02) {
      setError(`Amount cannot exceed balance due (${formatInr(pending)}).`);
      setPaying(false);
      return;
    }

    try {
      const orderRes = await fetch(`${url}/invoice-pay/${token}/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: amountToPay }),
      });
      const orderData = await orderRes.json();
      if (!orderRes.ok) {
        setError(orderData.message || "Could not start payment.");
        setPaying(false);
        return;
      }

      const Razorpay = await loadRazorpayScript();

      const options = {
        key: orderData.razorpay_key_id,
        amount: orderData.amount,
        currency: orderData.currency || "INR",
        name: invoice.business_name || "Invoice payment",
        description: `Invoice ${invoice.invoice_number}`,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${url}/invoice-pay/${token}/verify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            const verifyData = await verifyRes.json();
            if (!verifyRes.ok || !verifyData.success) {
              setError(verifyData.message || "Payment verification failed.");
              setPaying(false);
              return;
            }
            setSuccess(verifyData.invoice || { invoice_number: invoice.invoice_number });
          } catch {
            setError("Payment verification failed. Contact support if amount was deducted.");
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
        theme: { color: "#2563eb" },
      };

      const rzp = new Razorpay(options);
      rzp.on("payment.failed", () => {
        setError("Payment failed. Please try again.");
        setPaying(false);
      });
      rzp.open();
    } catch (err) {
      setError(err.message || "Payment could not be started.");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle2 className="mx-auto text-green-600 mb-4" size={48} />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Payment successful</h1>
          <p className="text-slate-600 mb-1">
            Invoice <strong>{success.invoice_number}</strong>
          </p>
          {success.pending != null && parseFloat(success.pending) > 0 ? (
            <p className="text-sm text-amber-700 mt-2">
              Remaining balance: {formatInr(success.pending)}
            </p>
          ) : (
            <p className="text-sm text-green-700 mt-2">This invoice is fully paid.</p>
          )}
          <p className="text-xs text-slate-500 mt-4">A receipt has been sent to your email if on file.</p>
        </div>
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Unable to pay</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-lg mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-blue-600 text-white px-6 py-5">
          <p className="text-sm opacity-90">{invoice.business_name}</p>
          <h1 className="text-2xl font-bold mt-1">Pay invoice</h1>
          <p className="text-sm opacity-90 mt-1">{invoice.invoice_number}</p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <p className="text-sm text-slate-500">Bill to</p>
            <p className="font-medium text-slate-900">{invoice.customer_name}</p>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Invoice date</p>
              <p className="font-medium">{invoice.invoice_date || "—"}</p>
            </div>
            <div>
              <p className="text-slate-500">Due date</p>
              <p className="font-medium">{invoice.due_date || "—"}</p>
            </div>
          </div>

          {Array.isArray(invoice.items) && invoice.items.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left p-2">Item</th>
                    <th className="text-right p-2">Amt</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.slice(0, 8).map((row, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2">
                        {typeof row.item === "object"
                          ? row.item.title || row.item.label || "Item"
                          : row.item || "Item"}
                      </td>
                      <td className="p-2 text-right">{formatInr(row.amount || row.rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-between items-end border-t pt-4">
            <div>
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-lg font-semibold">{formatInr(invoice.grand_total)}</p>
              {invoice.amount_paid > 0 ? (
                <p className="text-xs text-slate-500 mt-1">
                  Paid so far: {formatInr(invoice.amount_paid)}
                </p>
              ) : null}
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Amount due</p>
              <p className="text-2xl font-bold text-blue-600">{formatInr(invoice.pending)}</p>
            </div>
          </div>

          {invoice.allow_partial && parseFloat(invoice.pending) > 1 ? (
            <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
              <p className="text-sm font-medium text-slate-700">Payment amount</p>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="payMode"
                  checked={payFull}
                  onChange={() => {
                    setPayFull(true);
                    setPayAmount(String(parseFloat(invoice.pending) || ""));
                  }}
                />
                Pay full balance ({formatInr(invoice.pending)})
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="payMode"
                  checked={!payFull}
                  onChange={() => setPayFull(false)}
                />
                Pay partial amount
              </label>
              {!payFull ? (
                <input
                  type="number"
                  min={invoice.min_payment || 1}
                  max={parseFloat(invoice.pending) || undefined}
                  step="0.01"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  placeholder={`Min ${formatInr(invoice.min_payment || 1)}`}
                />
              ) : null}
            </div>
          ) : null}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded p-2">{error}</p>
          )}

          <button
            type="button"
            onClick={handlePay}
            disabled={paying || parseFloat(invoice.pending) <= 0}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold py-3 rounded-lg transition"
          >
            {paying
              ? "Processing…"
              : `Pay ${formatInr(payFull ? invoice.pending : payAmount || invoice.pending)}`}
          </button>

          <p className="text-xs text-center text-slate-400">
            Secure payment powered by Razorpay · UPI, cards & netbanking
          </p>
        </div>
      </div>
    </div>
  );
};

export default PayInvoicePage;
